import { ReactElement, useState } from 'react'
import { useForm } from '@tanstack/react-form'
import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'

import axios, { pluckError } from 'src/utilities/http-client'
import { CancelIcon, SubmitIcon } from 'src/v3/core/icons'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'

import {
  fetchCredentialRecertificationCycles,
  fetchExternalCertificates,
  fetchLicenseRecertificationCycles,
  fetchNREMTRecertificationCycles,
  fetchRecertificationCycleCategories,
  fetchSections,
  selectCredentialRecertificationCyclesStatus,
  selectExternalCertificates,
  selectExternalCertificatesStatus,
  selectLicenseRecertificationCyclesStatus,
  selectNREMTRecertificationCyclesStatus,
  selectRecertificationCategories,
  selectRecertificationCategoriesStatus,
  selectRecertificationCycles,
  selectSections,
  selectSectionsStatus,
} from 'src/v3/profile/profile.slice'
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  TextField,
  Typography,
} from '@mui/material'
import { useAutoFetch } from 'src/v3/hooks/use-auto-fetch'
import { useNotifications } from 'src/v3/hooks/use-notifications'
import {
  RecertificationCycleCategory,
  RecertificationCycleExternalCertificate,
} from 'src/v3/core/types'

dayjs.extend(isBetween)

type ExternalCertificateFormProps = {
  certificateID: string
  open: boolean
  setOpen: (status: boolean) => void
}

export let ExternalCertificateForm = ({
  certificateID,
  open,
  setOpen,
}: ExternalCertificateFormProps): ReactElement => {
  let dispatch = useAppDispatch()
  let { showError, NotificationComponent } = useNotifications()

  let externalCertificates = useAppSelector(selectExternalCertificates)
  let externalCertificatesStatus = useAppSelector(
    selectExternalCertificatesStatus,
  )
  let sections = useAppSelector(selectSections)
  let sectionsStatus = useAppSelector(selectSectionsStatus)
  let allCycles = useAppSelector(selectRecertificationCycles)
  let licensesStatus = useAppSelector(selectLicenseRecertificationCyclesStatus)
  let credentialsStatus = useAppSelector(
    selectCredentialRecertificationCyclesStatus,
  )
  let nremtStatus = useAppSelector(selectNREMTRecertificationCyclesStatus)
  let allCategories = useAppSelector(selectRecertificationCategories)
  let categoriesStatus = useAppSelector(selectRecertificationCategoriesStatus)
  let externalCertificate = externalCertificates.find(
    (c) => c.id === certificateID,
  )
  let sectionName = sections.find(
    (s) =>
      s.name.toLowerCase().replace(/\s+/g, '') ===
      externalCertificate?.hourType,
  )?.name

  let [saving, setSaving] = useState(false)

  useAutoFetch(licensesStatus, fetchLicenseRecertificationCycles)
  useAutoFetch(credentialsStatus, fetchCredentialRecertificationCycles)
  useAutoFetch(nremtStatus, fetchNREMTRecertificationCycles)
  useAutoFetch(externalCertificatesStatus, fetchExternalCertificates)
  useAutoFetch(sectionsStatus, fetchSections)

  let form = useForm({
    defaultValues: {
      recertificationCycle: '',
      categories: [
        {
          id: '',
          category: 'unselected',
          hours: externalCertificate?.length.toString() || '',
        },
      ],
    },
    onSubmit: async ({ value, formApi }) => {
      setSaving(true)

      let categories = allCategories[value.recertificationCycle || ''] || []
      let allExternalCerts = categories.flatMap(
        (cat) => cat.externalCertificates,
      )
      let allExternalCertsFiltered = allExternalCerts.filter(
        (ec) => ec.externalCertificateID === certificateID,
      )
      let ids = value.categories.map((catJoin) => catJoin.id)

      Promise.all([
        ...allExternalCertsFiltered
          .filter((ec) => !ids.includes(ec.id))
          .map((ec) =>
            axios.delete(
              `/api/profile/recertification-cycle/external-certificate/${ec.id}`,
            ),
          ),
        ...value.categories.map((cat) =>
          !cat.id
            ? axios.post<RecertificationCycleExternalCertificate>(
                `/api/profile/recertification-cycle/external-certificate`,
                {
                  externalCertificateID: certificateID,
                  categoryID: cat.category,
                  hours: cat.hours,
                },
              )
            : axios.put<RecertificationCycleExternalCertificate>(
                `/api/profile/recertification-cycle/external-certificate`,
                {
                  id: cat.id,
                  externalCertificateID: certificateID,
                  categoryID: cat.category,
                  hours: cat.hours,
                },
              ),
        ),
      ])
        .then(() =>
          dispatch(
            fetchRecertificationCycleCategories(value.recertificationCycle),
          ),
        )
        .then(() => setOpen(false))
        .then(() => formApi.reset())
        .catch((err) => {
          showError?.(
            pluckError(
              err,
              'Error Creating Recertification Cycle External Certificate',
            ),
          )
        })
        .finally(() => setSaving(false))
    },
  })

  let findCategory = (catList: RecertificationCycleCategory[]) => {
    let existingCategories = catList.filter((cat) =>
      cat.externalCertificates.some(
        (ec) => ec.externalCertificateID === certificateID,
      ),
    )
    let hours = form
      .getFieldValue('categories')
      .map((cat) => cat.hours)
      .reduce((sum, val) => sum + parseFloat(val), 0)
      .toString()
    let currentCategories = form.getFieldValue('categories')
    let catLen = currentCategories.length
    currentCategories.forEach(() => {
      form.removeFieldValue('categories', catLen - 1)
    })
    if (existingCategories.length > 0) {
      existingCategories.forEach((cat, i) => {
        let rcec = cat.externalCertificates.find(
          (ec) => ec.externalCertificateID === certificateID,
        )
        form.setFieldValue(`categories[${i}].id`, rcec?.id || '')
        form.setFieldValue(`categories[${i}].category`, cat.id)
        form.setFieldValue(
          `categories[${i}].hours`,
          rcec?.hours.toString() || '',
        )
      })
    } else {
      let match = catList.find((c) => c.name === sectionName)
      form.pushFieldValue('categories', {
        id: '',
        category: match && sectionName ? match.id : 'unselected',
        hours,
      })
    }
  }

  let handleOnClose = () => {
    form.reset()
    setOpen(false)
  }

  return (
    <Dialog open={open} onClose={handleOnClose}>
      <Paper sx={{ width: 600, px: 2, py: 1 }}>
        <DialogTitle sx={{ textAlign: 'center', mb: -3 }}>
          Add External Certificate
        </DialogTitle>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            e.stopPropagation()
            form.handleSubmit()
          }}
        >
          <DialogContent
            sx={{
              px: 2,
              pb: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            <Typography>1. Select Recertification Cycle</Typography>
            <form.Field
              name="recertificationCycle"
              validators={{
                onChange: ({ value }) =>
                  !value || value === 'unselected'
                    ? 'Recertification Cycle is a required field'
                    : undefined,
              }}
              listeners={{
                onChange: ({ value }) => {
                  if (
                    value &&
                    (!categoriesStatus[value] ||
                      categoriesStatus[value] === 'uninitialized')
                  ) {
                    dispatch(fetchRecertificationCycleCategories(value)).then(
                      ({ payload }) =>
                        findCategory(payload as RecertificationCycleCategory[]),
                    )
                  } else {
                    findCategory(allCategories[value] || [])
                  }
                },
              }}
              children={(field) => (
                <Autocomplete
                  value={allCycles.find((c) => field.state.value === c.id)}
                  onChange={(_, value) => {
                    field.handleChange(value?.id || '')
                  }}
                  options={allCycles.filter((cycle) =>
                    dayjs(externalCertificate?.dateCompleted).isBetween(
                      dayjs(cycle.issueDate),
                      dayjs(cycle.expirationDate),
                    ),
                  )}
                  getOptionKey={(option) => option.id}
                  getOptionLabel={(option) => option.name}
                  renderOption={(props, option) => {
                    // eslint-disable-next-line fp/no-rest-parameters
                    let { key, ...optionProps } = props
                    return (
                      <Typography
                        key={key}
                        {...optionProps}
                        sx={{ flex: '1 1 400px' }}
                      >
                        {option.name}
                      </Typography>
                    )
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      sx={{ bgcolor: 'background.paper' }}
                      label="Recertification Cycle"
                      error={field.state.meta.errors.length > 0}
                      helperText={field.state.meta.errors.join(', ')}
                    />
                  )}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value?.id
                  }
                />
              )}
            />
            <form.Subscribe
              selector={(state) => state.values.recertificationCycle}
              children={(rc) => {
                let hours = externalCertificate?.length || 0
                let categories = allCategories[rc] || []

                return (
                  <form.Field
                    name="categories"
                    mode="array"
                    validators={{
                      onSubmit: ({ value }) =>
                        value.reduce(
                          (sum, c) => sum + parseFloat(c.hours),
                          0,
                        ) !== hours
                          ? `All hours must be assigned & all category hours must add up to ${hours} hours.`
                          : undefined,
                    }}
                  >
                    {(field) => (
                      <>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-end',
                            gap: 0.5,
                          }}
                        >
                          <Typography sx={{ flexGrow: 1 }}>
                            2. Assign {hours} Hour{hours === 1 ? '' : 's'}
                          </Typography>
                          {}
                          <Button
                            color="secondary"
                            sx={{ mb: -0.25 }}
                            size="small"
                            onClick={() => {
                              field.pushValue({
                                id: '',
                                category: 'unselected',
                                hours: '',
                              })
                            }}
                          >
                            Add Category
                          </Button>
                          <Button
                            color="error"
                            sx={{ mb: -0.25 }}
                            size="small"
                            onClick={() =>
                              field.removeValue(field.state.value.length - 1)
                            }
                            disabled={field.state.value.length === 1}
                          >
                            Remove Category
                          </Button>
                        </Box>
                        {(!!rc && !categoriesStatus[rc]) ||
                        categoriesStatus[rc] === 'pending' ? (
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Skeleton
                              variant="rounded"
                              sx={{ width: '100%', height: 56 }}
                            />
                            <Skeleton
                              variant="rounded"
                              sx={{ width: '100%', height: 56 }}
                            />
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 1,
                            }}
                          >
                            {field.state.value.map((_, i) => (
                              <Box key={i} sx={{ display: 'flex', gap: 1 }}>
                                <form.Field
                                  name={`categories[${i}].category`}
                                  validators={{
                                    onSubmit: ({ value }) =>
                                      value === 'unselected'
                                        ? 'Category is a required field'
                                        : undefined,
                                  }}
                                  children={(subField) => (
                                    <FormControl
                                      fullWidth
                                      error={
                                        subField.state.meta.errors.length > 0
                                      }
                                    >
                                      <InputLabel>Category</InputLabel>
                                      <Select
                                        value={subField.state.value}
                                        label="Category"
                                        onChange={(e) =>
                                          subField.handleChange(e.target.value)
                                        }
                                        sx={{ bgcolor: 'background.paper' }}
                                      >
                                        <MenuItem value="unselected">
                                          Unselected
                                        </MenuItem>
                                        {categories
                                          .filter(
                                            (category) =>
                                              category.id ===
                                                subField.state.value ||
                                              !field.state.value
                                                .map((c) => c.category)
                                                .includes(category.id),
                                          )
                                          .map((category) => (
                                            <MenuItem
                                              key={category.id}
                                              value={category.id}
                                            >
                                              {category.name}{' '}
                                              {category.subcategory &&
                                                `(${category.subcategory})`}
                                            </MenuItem>
                                          ))}
                                      </Select>
                                      {subField.state.meta.errors.length >
                                        0 && (
                                        <FormHelperText>
                                          {subField.state.meta.errors.join(
                                            ', ',
                                          )}
                                        </FormHelperText>
                                      )}
                                    </FormControl>
                                  )}
                                />
                                <form.Field
                                  name={`categories[${i}].hours`}
                                  validators={{
                                    onSubmit: ({ value }) =>
                                      !parseFloat(value)
                                        ? 'Hours is a required field & must be greater than 0'
                                        : undefined,
                                  }}
                                  children={(subField) => (
                                    <TextField
                                      sx={{ bgcolor: 'background.paper' }}
                                      fullWidth
                                      variant="outlined"
                                      value={subField.state.value}
                                      label="Hours"
                                      name={subField.name}
                                      onChange={(e) => {
                                        subField.handleChange(
                                          e.target.value.replace(/[^\d.]/g, ''),
                                        )
                                        field.validate('change')
                                      }}
                                      onBlur={subField.handleBlur}
                                      error={
                                        subField.state.meta.errors.length > 0
                                      }
                                      helperText={subField.state.meta.errors.join(
                                        ', ',
                                      )}
                                    />
                                  )}
                                />
                              </Box>
                            ))}
                            {field.state.meta.errors.length > 0 && (
                              <FormHelperText sx={{ color: 'error.main' }}>
                                {field.state.meta.errors.join(', ')}
                              </FormHelperText>
                            )}
                          </Box>
                        )}
                      </>
                    )}
                  </form.Field>
                )
              }}
            />
          </DialogContent>
          <DialogActions
            sx={{ display: 'flex', justifyContent: 'center', pb: 1.5 }}
          >
            <form.Subscribe
              selector={(state) => state.isValid}
              children={(isValid) => (
                <Button
                  color="primary"
                  type="submit"
                  variant="contained"
                  sx={{ flex: '1 1 0px' }}
                  startIcon={<SubmitIcon />}
                  disabled={!isValid}
                  loading={saving}
                >
                  Submit
                </Button>
              )}
            />
            <Button
              color="error"
              onClick={handleOnClose}
              variant="contained"
              sx={{
                flex: '1 1 0px',
              }}
              startIcon={<CancelIcon />}
            >
              Cancel
            </Button>
          </DialogActions>
        </form>
        {NotificationComponent}
      </Paper>
    </Dialog>
  )
}
